import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import VoucherImageUpload from "components/Common/VoucherImageUpload"
import { useMemo, useCallback, useState, useEffect } from "react"
import { toast } from "react-toastify"
import { Form, FormFeedback, Label, Button, Row, Col, Card, CardBody } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import { useAccounts } from "hooks/useAccountEntity"
import { useAccountPaymentMethods } from "hooks/useAccountPaymentMethod"
import {
  useCreateAccountFundTransfer,
  useEditAccountFundTransfer,
} from "hooks/useAccountFundTransfer"

const AccountFundTransferAddEdit = ({ modal, toggleModal, isEdit, editedData }) => {
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateAccountFundTransfer()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditAccountFundTransfer()

  const [voucherImages, setVoucherImages] = useState(editedData?.images || [])

  useEffect(() => {
    setVoucherImages(editedData?.images || [])
  }, [editedData])

  const { data: accountResponse, isLoading: accountLoading } =
    useAccounts({ all: true, per_page: 200 }, modal)
  const { data: paymentMethodResponse, isLoading: paymentMethodLoading } =
    useAccountPaymentMethods({ all: true, per_page: 200 }, modal)

  const accounts = accountResponse?.data?.data || accountResponse?.data || []
  const paymentMethods = paymentMethodResponse?.data?.data || paymentMethodResponse?.data || []

  const accountOptions = useMemo(
    () =>
      Array.isArray(accounts)
        ? accounts.map(item => ({
            value: item.id,
            label: item.custodian?.name
              ? `${item.name} (${item.custodian.name})`
              : item.name,
          }))
        : [],
    [accounts]
  )

  const paymentMethodOptions = useMemo(
    () =>
      Array.isArray(paymentMethods)
        ? paymentMethods.map(item => ({ value: item.id, label: item.name }))
        : [],
    [paymentMethods]
  )

  const validationSchema = Yup.object({
    from_account_id: Yup.mixed().required("Please select a from account"),
    to_account_id: Yup.mixed()
      .required("Please select a to account")
      .test("different-accounts", "From and to accounts must be different", function (value) {
        return !value || !this.parent.from_account_id || String(value) !== String(this.parent.from_account_id)
      }),
    amount: Yup.number().typeError("Amount must be numeric").integer("Amount must be a whole number").min(1, "Amount must be at least 1").required("Please enter amount"),
    payment_method_id: Yup.mixed().required("Please select a payment method"),
    transfer_date: Yup.string().required("Please select transfer date"),
    reference_number: Yup.string().nullable().max(255, "Reference number cannot exceed 255 characters"),
    description: Yup.string().nullable(),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        ...values,
        amount: Number(values.amount),
        reference_number: values.reference_number?.trim() || null,
        description: values.description?.trim() || null,
        image_ids: voucherImages.map(img => img.id),
      }

      const action = isEdit
        ? editMutation({ id: editedData.id, ...payload })
        : createMutation(payload)

      try {
        const res = await action
        toast.success(res?.message ?? `Fund transfer ${isEdit ? "updated" : "created"} successfully.`)
        resetForm()
        toggleModal()
      } catch (error) {
        const msg = error?.response?.data?.message || "Failed to save fund transfer."
        toast.error(msg)
      }
    },
    [isEdit, editedData, editMutation, createMutation, toggleModal, voucherImages]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      from_account_id: editedData?.from_account_id || editedData?.fromAccount?.id || "",
      to_account_id: editedData?.to_account_id || editedData?.toAccount?.id || "",
      amount: editedData?.amount ?? "",
      payment_method_id: editedData?.payment_method_id || editedData?.paymentMethod?.id || "",
      transfer_date: editedData?.transfer_date || "",
      reference_number: editedData?.reference_number || "",
      description: editedData?.description || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const loadingAny = createLoading || editLoading || accountLoading || paymentMethodLoading

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Fund Transfer" : "Add Fund Transfer"}
      size="lg"
    >
      <Form onSubmit={e => { e.preventDefault(); formik.handleSubmit() }}>
        <Card className="border">
          <CardBody>
            <h5 className="mb-3">Transfer Information</h5>
            <Row>
              <Col md={6}>
                <FormikCreatableSelect name="from_account_id" formik={formik} label="From Account" options={accountOptions} loading={accountLoading} required />
              </Col>
              <Col md={6}>
                <FormikCreatableSelect name="to_account_id" formik={formik} label="To Account" options={accountOptions} loading={accountLoading} required />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="amount">Amount</Label>
                  <InputField type="number" name="amount" id="amount" min="1" value={formik.values.amount} onChange={formik.handleChange} invalid={formik.touched.amount && !!formik.errors.amount} />
                  {formik.touched.amount && formik.errors.amount && <FormFeedback type="invalid">{formik.errors.amount}</FormFeedback>}
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="transfer_date">Transfer Date</Label>
                  <InputField type="date" name="transfer_date" id="transfer_date" value={formik.values.transfer_date} onChange={formik.handleChange} invalid={formik.touched.transfer_date && !!formik.errors.transfer_date} />
                  {formik.touched.transfer_date && formik.errors.transfer_date && <FormFeedback type="invalid">{formik.errors.transfer_date}</FormFeedback>}
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormikCreatableSelect name="payment_method_id" formik={formik} label="Payment Method" options={paymentMethodOptions} loading={paymentMethodLoading} required />
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="reference_number">Reference No.</Label>
                  <InputField type="text" name="reference_number" id="reference_number" value={formik.values.reference_number} onChange={formik.handleChange} invalid={formik.touched.reference_number && !!formik.errors.reference_number} />
                  {formik.touched.reference_number && formik.errors.reference_number && <FormFeedback type="invalid">{formik.errors.reference_number}</FormFeedback>}
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card className="border mt-3">
          <CardBody>
            <h5 className="mb-3">Additional Details</h5>
            <div className="mb-3">
              <Label htmlFor="description">Description</Label>
              <InputField type="textarea" name="description" id="description" value={formik.values.description} onChange={formik.handleChange} invalid={formik.touched.description && !!formik.errors.description} />
              {formik.touched.description && formik.errors.description && <FormFeedback type="invalid">{formik.errors.description}</FormFeedback>}
            </div>
            <VoucherImageUpload images={voucherImages} onChange={setVoucherImages} label="Voucher Images" />
          </CardBody>
        </Card>

        <div className="text-end mt-3">
          <Button color="success" type="submit" disabled={loadingAny}>
            {loadingAny ? "Saving..." : isEdit ? "Save Changes" : "Add Fund Transfer"}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default AccountFundTransferAddEdit
